import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiMail, FiArrowLeft, FiCheck, FiLock } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Reset-with-token mode state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!response.ok) throw new Error('Failed to send reset email');
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Invalid or expired reset link');
      }
      setResetDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8] flex flex-col">
      <header className="bg-[#1e3a5f] py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-bold text-xl text-[#f5f0e6]">ConnectMakeLaunch AI</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* ---- RESET-WITH-TOKEN MODE ---- */}
            {token ? (
              resetDone ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCheck className="text-3xl text-green-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Password updated</h1>
                  <p className="text-[#1e3a5f]/60 mb-6">You can now log in with your new password.</p>
                  <button onClick={() => navigate('/login')} className="text-[#1e3a5f] font-medium hover:underline flex items-center justify-center gap-2 mx-auto" data-testid="reset-back-login-btn">
                    <FiArrowLeft /> Back to login
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Choose a new password</h1>
                  <p className="text-[#1e3a5f]/60 mb-6">Enter a new password for your account.</p>
                  {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="reset-error">{error}</div>}
                  <form onSubmit={handleReset} className="space-y-4">
                    <div className="relative">
                      <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors" data-testid="new-password-input" />
                    </div>
                    <div className="relative">
                      <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors" data-testid="confirm-password-input" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-[#1e3a5f] hover:bg-[#2a4a73] text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50" data-testid="reset-password-submit-btn">
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </>
              )
            ) : sent ? (
              /* ---- REQUEST SENT ---- */
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCheck className="text-3xl text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Check your email</h1>
                <p className="text-[#1e3a5f]/60 mb-6">If an account exists for {email}, we've sent a password reset link.</p>
                <button onClick={() => navigate('/login')} className="text-[#1e3a5f] font-medium hover:underline flex items-center justify-center gap-2 mx-auto">
                  <FiArrowLeft /> Back to login
                </button>
              </div>
            ) : (
              /* ---- REQUEST FORM ---- */
              <>
                <button onClick={() => navigate('/login')} className="text-[#1e3a5f]/70 hover:text-[#1e3a5f] flex items-center gap-2 mb-6 transition-colors">
                  <FiArrowLeft /> Back to login
                </button>
                <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Reset Password</h1>
                <p className="text-[#1e3a5f]/60 mb-6">Enter your email and we'll send you a link to reset your password.</p>
                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
                <form onSubmit={handleRequest} className="space-y-4">
                  <div className="relative">
                    <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors" data-testid="reset-email-input" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-[#1e3a5f] hover:bg-[#2a4a73] text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50" data-testid="reset-submit-btn">
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
